import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, inject, signal, viewChild } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { GAMES, createGame, type Game, type GameHost, type GameId, type Rect } from "./nl-games";

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  /** frames until this particle may start a strike again */
  cd: number;
  /** frames of afterglow after being hit by a strike */
  glow: number;
  /** part of the typed word: springs to (tx, ty) instead of drifting */
  formed: boolean;
  tx: number;
  ty: number;
  /** > 0: heading for a random spot after leaving a word; counts down, then the particle drifts freely again */
  scatter: number;
}

/** One straight piece of a strike. `at` is its distance along the channel from the strike origin. */
interface Segment {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  at: number;
  /** 0 = main channel, 1+ = branches (thinner, dimmer) */
  depth: number;
}

/**
 * A lightning strike. Its geometry is generated once where the contact happened
 * and never follows the particles, so it cannot stretch across the screen. It
 * GROWS from the origin along its channels (segments appear as `age * SPEED`
 * passes their `at`), flickers while it holds, then fades.
 */
interface Strike {
  segments: Segment[];
  /** distance of the farthest segment: once reached, the strike starts fading */
  reach: number;
  age: number;
  /** frames left after it fully grew */
  hold: number;
  /** particles the channels end on, lit when the front gets there */
  hits: { p: Particle; at: number }[];
  /** where it started: the flash sits here */
  ox: number;
  oy: number;
}

/** One particle per this many px² of stage, at full quality. */
const PX_PER_PARTICLE = 550;

/**
 * Quality tiers. The field starts at the best one the machine seems able to hold and steps
 * DOWN when frames run slow (see `govern`); the tier is remembered per browser. Touch and link
 * distances grow as the field thins, so a sparser field keeps a similar neighbourhood. `lite`
 * also drops the card's backdrop blur (CSS, `data-fx` on the stage): a blur re-done over a
 * canvas that repaints 60 times a second is what low-end GPUs choke on first.
 */
interface Tier {
  cap: number;
  density: number;
  /** highest device-pixel ratio and canvas backing-store size */
  dpr: number;
  pixels: number;
  link: number;
  /** most link segments drawn in a frame: thicker clusters are thinned evenly, not allowed to cost more */
  maxLinks: number;
  contact: number;
  lite: boolean;
}
const TIERS: Tier[] = [
  { cap: 2800, density: 1, dpr: 2, pixels: 4_000_000, link: 34, maxLinks: 6000, contact: 10, lite: false },
  { cap: 1400, density: 0.5, dpr: 1.5, pixels: 2_500_000, link: 42, maxLinks: 3500, contact: 12, lite: false },
  { cap: 700, density: 0.25, dpr: 1, pixels: 1_600_000, link: 54, maxLinks: 1800, contact: 16, lite: true },
  { cap: 340, density: 0.12, dpr: 1, pixels: 1_000_000, link: 72, maxLinks: 900, contact: 20, lite: true },
];
const TIER_KEY = "nl-fx-tier";
/** Frames per measuring window, and the average frame time (ms) over which a window counts as slow (~45fps). */
const GOVERN_FRAMES = 60;
const GOVERN_SLOW_MS = 22;
/** Frames a set of link pairs is reused for. */
const LINK_REUSE = 3;

const SPEED = 7; // px of channel revealed per frame: slow enough to watch it grow
const HOLD = 50; // frames a grown strike flickers and fades before it is gone
const MAX_STRIKES = 2;
/** Strikes sit in the background, so they are drawn dimmer than the foreground field. */
const STRIKE_DIM = 0.65;
/** Steps (60Hz) between strikes: 3 to 6 seconds. */
const STRIKE_GAP_MIN = 180;
const STRIKE_GAP_RANGE = 180;
const MAX_SPEED = 2.6;
/** Physics is tuned per frame at 60Hz; the loop runs it on a fixed step so 120Hz+ screens get the same speed and cost. */
const STEP_MS = 1000 / 60;
/** Cap on the canvas backing store, so huge screens do not pay for a 6016x3208 @2x surface. */
const LINK_ALPHA_BUCKETS = 6;
/** Press shorter than this many steps is a click (shockwave); longer is a hold (pull). */
const HOLD_STEPS = 15;
const PULL_RADIUS = 280;
/** Typed-word effect: dot spacing of the sampled glyphs, the share of particles the word may take, longest word. */
const WORD_STEP = 6;
const WORD_SHARE = 0.75;
const WORD_MAX_CHARS = 24;
const WORD_SPRING = 0.012;
const WORD_DAMP = 0.9;
const WORD_MAX_SPEED = 14;
/** Dots of the word link only to neighbours this close. */
const WORD_LINK = 13;
/** Longest wait before a burst of keystrokes re-spells the word. */
const WORD_DEBOUNCE_MS = 50;
/** How far (in grid cells) a dot looks for a free particle before taking any. */
const WORD_SEARCH_RINGS = 8;

/** Snake easter egg (desktop only). Distances in px, speeds in px per 60Hz step. */
const SNAKE_BASE_SPEED = 2.6;
const SNAKE_MAX_SPEED = 5.4;
/** Head positions are stored this far apart, so the body length does not depend on the speed. */
const SNAKE_SPACING = 4;
const SNAKE_START_LEN = 30;
/** Body points added per particle eaten. */
const SNAKE_GROW = 6;
const SNAKE_EAT = 10;
const SNAKE_RADIUS = 7;
/** Particles inside this radius of the head lean toward it. */
const SNAKE_PULL = 60;
/** Recent body points the head cannot collide with (its own neck). */
const SNAKE_NECK = 12;
/** Particles eaten per level; each level is faster and adds a toxic particle. */
const LEVEL_EVERY = 12;
const SPEED_PER_LEVEL = 0.3;
const MAX_TOXIC = 8;
const BEST_KEY = "nl-snake-best";
const DESKTOP = "(min-width: 1024px) and (hover: hover) and (pointer: fine)";

/** Red particle that ends the game on touch. */
interface Toxic {
  x: number;
  y: number;
  vx: number;
  vy: number;
}

interface Snake {
  x: number;
  y: number;
  /** heading and the heading the arrows asked for, in radians; the head turns toward `want` at a limited rate */
  angle: number;
  want: number;
  /** stored head positions, flat [x0, y0, x1, y1…], newest last; the body is the last `len` points */
  pts: number[];
  len: number;
  score: number;
  level: number;
  best: number;
  toxics: Toxic[];
  /** 0 while alive; counts the steps since the bite / toxic hit (drives the game-over card) */
  over: number;
}

const ARROWS: Record<string, number> = {
  ArrowRight: 0,
  ArrowDown: Math.PI / 2,
  ArrowLeft: Math.PI,
  ArrowUp: -Math.PI / 2,
};

/** Smallest signed angle from `from` to `to`, in (-π, π]. */
const angleDiff = (from: number, to: number): number => {
  const d = (to - from) % (Math.PI * 2);
  return d > Math.PI ? d - Math.PI * 2 : d <= -Math.PI ? d + Math.PI * 2 : d;
};

/**
 * Interactive particle field behind the Nightline login.
 *
 * Particles drift in the signal colour, link up when close and bounce off the
 * edges (they never leave the stage). Near the cursor they are pushed away and
 * wire themselves to it; a click sends a shockwave and holding the button pulls them in. Two particles that touch
 * fire a branching lightning strike that spreads to their neighbours.
 * Easter egg (desktop only): the Play button, or an arrow key pressed while no
 * field has focus, starts a snake the arrows steer. It eats the particles; each
 * level is faster and adds a toxic red particle (Esc leaves).
 * Runs outside Angular (no change detection per frame), pauses while the tab is
 * hidden and renders a single still frame under `prefers-reduced-motion`
 * (no game in that case).
 */
@Component({
  selector: "app-nl-particles",
  template: `<canvas #canvas aria-hidden="true"></canvas>`,
  styles: [
    `
      :host {
        position: absolute;
        inset: 0;
        z-index: -1;
        pointer-events: none;
      }
      canvas {
        display: block;
        width: 100%;
        height: 100%;
      }
    `,
  ],
})
export class NlParticlesComponent implements AfterViewInit, OnDestroy, GameHost {
  private readonly zone = inject(NgZone);
  private readonly translate = inject(TranslateService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly canvasRef = viewChild.required<ElementRef<HTMLCanvasElement>>("canvas");

  private ctx: CanvasRenderingContext2D | null = null;
  particles: Particle[] = [];
  private strikes: Strike[] = [];
  /** frames until any new strike may fire, so they come one after another, not in bursts */
  private strikeGap = 0;
  width = 0;
  height = 0;
  private dpr = 1;
  private frame = 0;
  tick = 0;
  color = "rgb(120, 200, 255)";
  /** pointer on the stage; `vx`/`vy` are its smoothed velocity in px per event */
  mouse = { x: 0, y: 0, active: false, vx: 0, vy: 0 };
  private shock: { x: number; y: number; t: number } | null = null;
  /** pointer held down on the stage: `t` counts steps, past HOLD_STEPS it pulls */
  private hold: { t: number } | null = null;
  /** what the username field says right now, trimmed: the particles spell it behind the card */
  private word = "";
  private wordTimer = 0;
  /** pending re-spell, so a burst of keystrokes costs one pass */
  private wordPending = 0;
  private formedCount = 0;
  private glyphCanvas: HTMLCanvasElement | null = null;
  private readonly motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
  private reducedMotion = this.motionQuery.matches;
  private lastTs = 0;
  private acc = 0;
  private resizeObserver?: ResizeObserver;
  private snake: Snake | null = null;
  /** the active mini-game other than snake (see nl-games.ts) */
  private game: Game | null = null;
  readonly games = GAMES;
  readonly activeId = signal<GameId | null>(null);
  private cardRect: Rect | null = null;
  private cardAt = -999;
  private readonly desktopQuery = window.matchMedia(DESKTOP);
  /** a game is on screen; read by the login page for its hint */
  readonly playing = signal(false);
  /** particle count the stage should hold; the snake eats below it and the field refills */
  private target = 0;
  private tier = this.initialTier();
  private govAcc = 0;
  private govN = 0;
  /** measuring windows to skip after start-up or a tier change (compilation, layout and resizes are slow once) */
  private govGrace = 2;
  /** Link pairs found by the grid search, reused for a few frames: particles move ~0.5px a frame, so a pair stays a pair. */
  /** [0] the free field, [1] the dots of the typed word (a second, short-range pass) */
  private linkSets: { a: Particle[]; b: Particle[]; k: number[] }[] = [
    { a: [], b: [], k: [] },
    { a: [], b: [], k: [] },
  ];
  private linkAge = 0;
  private linkReach = 0;
  private linkWord = false;
  private contactSkip = false;
  private pairHead = new Int32Array(0);
  private pairNext = new Int32Array(0);

  private readonly onMove = (e: PointerEvent) => {
    const rect = this.host.nativeElement.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const m = this.mouse;
    // smoothed velocity: the pinball flipper and the gravity fling read it
    this.mouse = { x, y, active: true, vx: m.active ? m.vx * 0.6 + (x - m.x) * 0.4 : 0, vy: m.active ? m.vy * 0.6 + (y - m.y) * 0.4 : 0 };
  };
  private readonly onLeave = () => (this.mouse.active = false);
  private readonly onDown = (e: PointerEvent) => {
    const rect = this.host.nativeElement.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    this.mouse = { ...this.mouse, x, y, active: true };
    // Pressing a field or a button is not a gesture on the stage: no pull there.
    if (!(e.target as HTMLElement | null)?.closest("input, textarea, select, button, a, mat-select")) {
      this.hold = { t: 0 };
      this.game?.pointerDown(x, y);
    }
  };
  /** A short press repels (shockwave where it was released); a long one already pulled and just ends. */
  private readonly onUp = (e: PointerEvent) => {
    const rect = this.host.nativeElement.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    if (this.game) {
      if (this.hold) this.game.pointerUp(x, y);
    } else if (this.hold && this.hold.t <= HOLD_STEPS) {
      this.shock = { x, y, t: 0 };
    }
    this.hold = null;
  };
  private readonly onCancel = () => (this.hold = null);
  /** The login's first text field is the username. */
  private usernameField(): HTMLInputElement | null {
    return this.host.nativeElement.closest(".nl-login")?.querySelector<HTMLInputElement>("app-global-login input") ?? null;
  }
  private readonly onInput = (e: Event) => {
    if (e.target && e.target === this.usernameField()) {
      this.syncWord();
    }
  };
  private readonly onFocusIn = (e: FocusEvent) => {
    if (e.target && e.target === this.usernameField()) {
      this.syncWord();
    }
  };
  private readonly onKey = (e: KeyboardEvent) => {
    if (!this.canPlay() || e.ctrlKey || e.metaKey || e.altKey) {
      return;
    }
    if (e.key === "Escape") {
      this.stop();
      return;
    }
    const dir = ARROWS[e.key];
    if (this.game) {
      return;
    }
    const t = e.target as HTMLElement | null;
    // Arrows belong to the fields while one has focus (caret, selects, the code boxes).
    if (dir === undefined || t?.closest("input, textarea, select, [contenteditable], mat-select")) {
      return;
    }
    e.preventDefault();
    const sn = this.snake;
    if (!sn || sn.over > 0) {
      this.start(dir);
    } else if (Math.abs(angleDiff(sn.angle, dir)) < 2.6) {
      // a reversal would bite the neck: ignore it
      sn.want = dir;
    }
  };
  private readonly onVisibility = () => this.restart();
  private readonly onMotionChange = () => {
    this.reducedMotion = this.motionQuery.matches;
    this.restart();
  };

  /** (Re)starts the loop, or paints one still frame when motion is reduced. */
  private restart(): void {
    cancelAnimationFrame(this.frame);
    this.lastTs = 0;
    this.acc = 0;
    if (this.reducedMotion) {
      this.draw();
    } else if (!document.hidden) {
      this.frame = requestAnimationFrame(this.loop);
    }
  }

  ngAfterViewInit(): void {
    this.ctx = this.canvasRef().nativeElement.getContext("2d");
    if (!this.ctx) {
      return;
    }
    this.zone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver(() => this.resize());
      this.resizeObserver.observe(this.host.nativeElement);
      this.resize();
      this.readColor();
      this.motionQuery.addEventListener("change", this.onMotionChange);
      window.addEventListener("pointermove", this.onMove, { passive: true });
      window.addEventListener("pointerdown", this.onDown, { passive: true });
      window.addEventListener("pointerup", this.onUp, { passive: true });
      window.addEventListener("pointercancel", this.onCancel, { passive: true });
      window.addEventListener("blur", this.onCancel);
      window.addEventListener("keydown", this.onKey);
      window.addEventListener("input", this.onInput);
      window.addEventListener("focusin", this.onFocusIn);
      // the field may come prefilled (remembered user, autofill) without an input event
      this.wordTimer = window.setTimeout(() => this.syncWord(), 600);
      document.documentElement.addEventListener("pointerleave", this.onLeave);
      document.addEventListener("visibilitychange", this.onVisibility);
      this.restart();
    });
  }

  ngOnDestroy(): void {
    cancelAnimationFrame(this.frame);
    this.game?.stop();
    this.resizeObserver?.disconnect();
    this.motionQuery.removeEventListener("change", this.onMotionChange);
    window.removeEventListener("pointermove", this.onMove);
    window.removeEventListener("pointerdown", this.onDown);
    window.removeEventListener("pointerup", this.onUp);
    window.removeEventListener("pointercancel", this.onCancel);
    window.removeEventListener("blur", this.onCancel);
    window.removeEventListener("keydown", this.onKey);
    window.removeEventListener("input", this.onInput);
    window.removeEventListener("focusin", this.onFocusIn);
    window.clearTimeout(this.wordTimer);
    window.clearTimeout(this.wordPending);
    document.documentElement.removeEventListener("pointerleave", this.onLeave);
    document.removeEventListener("visibilitychange", this.onVisibility);
  }

  /** The signal is a relative OKLCH colour; a probe element resolves it to something the canvas parses. */
  private readColor(): void {
    const probe = document.createElement("span");
    probe.style.color = "var(--nl-signal)";
    this.host.nativeElement.appendChild(probe);
    this.color = getComputedStyle(probe).color || this.color;
    probe.remove();
  }

  private makeParticle(x = Math.random() * this.width, y = Math.random() * this.height): Particle {
    const angle = Math.random() * Math.PI * 2;
    const speed = 0.15 + Math.random() * 0.35;
    return {
      x,
      y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      r: 0.6 + Math.random() * 1.1,
      cd: Math.round(Math.random() * 60),
      glow: 0,
      formed: false,
      tx: 0,
      ty: 0,
      scatter: 0,
    };
  }

  private initialTier(): number {
    let stored = 0;
    try {
      stored = Number(localStorage.getItem(TIER_KEY)) || 0;
    } catch {
      // storage blocked: start from the machine's own hints
    }
    const weak = (navigator.hardwareConcurrency ?? 8) <= 4 || ((navigator as { deviceMemory?: number }).deviceMemory ?? 8) <= 4;
    return Math.min(TIERS.length - 1, Math.max(stored, weak ? 1 : 0));
  }

  /** Counts slow frames; after a slow window the field gets one tier lighter, for good. */
  private govern(dt: number): void {
    if (document.hidden || dt > 250) {
      return; // a background tab or a stall is not the field's doing
    }
    this.govAcc += dt;
    if (++this.govN < GOVERN_FRAMES) {
      return;
    }
    const avg = this.govAcc / this.govN;
    this.govAcc = 0;
    this.govN = 0;
    if (this.govGrace > 0) {
      this.govGrace--;
    } else if (avg > GOVERN_SLOW_MS && this.tier < TIERS.length - 1) {
      this.tier++;
      this.govGrace = 1;
      try {
        localStorage.setItem(TIER_KEY, String(this.tier));
      } catch {
        // the tier just lasts this visit
      }
      this.resize();
    }
  }

  private resize(): void {
    const canvas = this.canvasRef().nativeElement;
    const oldW = this.width;
    const oldH = this.height;
    this.width = this.host.nativeElement.clientWidth;
    this.height = this.host.nativeElement.clientHeight;
    if (oldW > 0 && oldH > 0 && (oldW !== this.width || oldH !== this.height)) {
      // a window that grows (maximise, devtools closing) would otherwise leave everyone in the old top-left corner
      const sx = this.width / oldW;
      const sy = this.height / oldH;
      for (const p of this.particles) {
        p.x *= sx;
        p.y *= sy;
      }
    }
    const t = TIERS[this.tier];
    const budget = Math.sqrt(t.pixels / Math.max(1, this.width * this.height));
    // below 1 the canvas is stretched by CSS: soft, but on a huge screen that is the price of holding the frame rate
    this.dpr = Math.max(0.6, Math.min(window.devicePixelRatio || 1, t.dpr, budget));
    this.host.nativeElement.closest(".nl-login")?.setAttribute("data-fx", t.lite ? "lite" : "full");
    canvas.width = Math.round(this.width * this.dpr);
    canvas.height = Math.round(this.height * this.dpr);
    this.ctx?.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);

    const target = Math.min(t.cap, Math.round((this.width * this.height) / (PX_PER_PARTICLE / t.density)));
    this.target = target;
    while (this.particles.length < target) {
      this.particles.push(this.makeParticle());
    }
    this.particles.length = target;
    // a shrink must not strand anyone outside the new bounds
    for (const p of this.particles) {
      p.x = Math.min(Math.max(p.x, p.r), this.width - p.r);
      p.y = Math.min(Math.max(p.y, p.r), this.height - p.r);
    }
    if (this.word) {
      this.formWord();
    }
    if (this.reducedMotion) {
      this.draw();
    }
  }

  private loop = (ts: number): void => {
    this.frame = requestAnimationFrame(this.loop);
    if (this.lastTs) {
      this.govern(ts - this.lastTs);
    }
    this.acc += this.lastTs ? Math.min(ts - this.lastTs, STEP_MS * 3) : STEP_MS;
    this.lastTs = ts;
    // Frames that arrive before the next 60Hz step are skipped entirely: no step, no draw.
    // The small slack keeps a 60Hz display from skipping on timestamp jitter.
    if (this.acc < STEP_MS - 1) {
      return;
    }
    this.acc = Math.max(0, this.acc - STEP_MS);
    this.step();
    this.draw();
    // Theme toggles and company changes move the signal; re-read it every few seconds (it forces a style recalculation).
    if (++this.tick % 180 === 0) {
      this.readColor();
    }
  };

  // ------------------------------------------------------------------ physics

  private step(): void {
    const { width: w, height: h, mouse, shock } = this;
    if (shock) {
      shock.t += 1;
      if (shock.t > 40) {
        this.shock = null;
      }
    }
    if (this.hold) {
      this.hold.t++;
    }
    // while a game runs it owns the pointer: no pull, no repel, no shockwave
    const pulling = !this.game && !!this.hold && this.hold.t > HOLD_STEPS && mouse.active;
    for (const p of this.particles) {
      if (pulling) {
        const dx = mouse.x - p.x;
        const dy = mouse.y - p.y;
        const d2 = dx * dx + dy * dy;
        if (d2 < PULL_RADIUS * PULL_RADIUS && d2 > 1) {
          const d = Math.sqrt(d2);
          const f = (1 - d / PULL_RADIUS) * 0.4;
          // inward pull plus a sideways swirl, so they orbit the cursor instead of stacking on it
          p.vx += (dx / d) * f - (dy / d) * f * 0.45;
          p.vy += (dy / d) * f + (dx / d) * f * 0.45;
          if (d < 30) {
            p.vx *= 0.9;
            p.vy *= 0.9;
          }
        }
      } else if (mouse.active && !this.game) {
        const dx = p.x - mouse.x;
        const dy = p.y - mouse.y;
        const d2 = dx * dx + dy * dy;
        if (d2 < 150 * 150 && d2 > 1) {
          const d = Math.sqrt(d2);
          const f = (1 - d / 150) * 0.45;
          p.vx += (dx / d) * f;
          p.vy += (dy / d) * f;
        }
      }
      if (shock) {
        const dx = p.x - shock.x;
        const dy = p.y - shock.y;
        const d = Math.sqrt(dx * dx + dy * dy) || 1;
        if (Math.abs(d - shock.t * 14) < 40) {
          p.vx += (dx / d) * 1.8;
          p.vy += (dy / d) * 1.8;
        }
      }

      if (p.formed) {
        // a spring to its dot of the word, damped, with a little shimmer so the letters stay alive
        p.vx = (p.vx + (p.tx - p.x) * WORD_SPRING + (Math.random() - 0.5) * 0.06) * WORD_DAMP;
        p.vy = (p.vy + (p.ty - p.y) * WORD_SPRING + (Math.random() - 0.5) * 0.06) * WORD_DAMP;
        const fs = Math.hypot(p.vx, p.vy);
        if (fs > WORD_MAX_SPEED) {
          p.vx *= WORD_MAX_SPEED / fs;
          p.vy *= WORD_MAX_SPEED / fs;
        }
        if (p.scatter > 0 && --p.scatter === 0) {
          p.formed = false;
        }
      } else {
        // Brownian drift keeps the field alive; drag pulls kicks back to a cruise.
        p.vx = p.vx * 0.985 + (Math.random() - 0.5) * 0.03;
        p.vy = p.vy * 0.985 + (Math.random() - 0.5) * 0.03;
        const sp = Math.hypot(p.vx, p.vy);
        if (sp > MAX_SPEED) {
          p.vx *= MAX_SPEED / sp;
          p.vy *= MAX_SPEED / sp;
        } else if (sp < 0.12) {
          const a = Math.random() * Math.PI * 2;
          p.vx += Math.cos(a) * 0.05;
          p.vy += Math.sin(a) * 0.05;
        }
      }

      p.x += p.vx;
      p.y += p.vy;

      // Bounce off the edges: reflect the velocity, lose a little energy, stay inside.
      if (p.x < p.r) {
        p.x = p.r;
        p.vx = Math.abs(p.vx) * 0.9;
      } else if (p.x > w - p.r) {
        p.x = w - p.r;
        p.vx = -Math.abs(p.vx) * 0.9;
      }
      if (p.y < p.r) {
        p.y = p.r;
        p.vy = Math.abs(p.vy) * 0.9;
      } else if (p.y > h - p.r) {
        p.y = h - p.r;
        p.vy = -Math.abs(p.vy) * 0.9;
      }

      if (p.cd > 0) p.cd--;
      if (p.glow > 0) p.glow--;
    }

    this.stepSnake();
    this.game?.step();
    this.contacts();
    for (const s of this.strikes) {
      s.age++;
      if (s.age * SPEED >= s.reach) s.hold--;
      for (const hit of s.hits) {
        if (hit.p.glow === 0 && s.age * SPEED >= hit.at && s.age * SPEED < hit.at + SPEED) {
          hit.p.glow = 18;
        }
      }
    }
    this.strikes = this.strikes.filter((s) => s.hold > 0);
  }

  /** Fills `set` with the pairs of `list` closer than `range`, thinned evenly to the tier's link budget. */
  private buildLinks(set: { a: Particle[]; b: Particle[]; k: number[] }, range: number, list: Particle[]): void {
    set.a.length = set.b.length = set.k.length = 0;
    this.forPairs(
      range,
      (p, q, d2) => {
        set.a.push(p);
        set.b.push(q);
        set.k.push(Math.min(LINK_ALPHA_BUCKETS - 1, Math.floor((1 - Math.sqrt(d2) / range) * LINK_ALPHA_BUCKETS)));
      },
      list,
    );
    // over the budget (a dense cluster): keep every n-th pair, evenly, so the cost stays bounded
    const keep = Math.ceil(set.a.length / TIERS[this.tier].maxLinks);
    if (keep > 1) {
      let w = 0;
      for (let r = 0; r < set.a.length; r += keep, w++) {
        set.a[w] = set.a[r];
        set.b[w] = set.b[r];
        set.k[w] = set.k[r];
      }
      set.a.length = set.b.length = set.k.length = w;
    }
  }

  /**
   * Calls `fn` once per pair closer than `range`. A uniform grid with cells of
   * `range` px means each particle only meets the 3x3 cells around it, so the
   * cost is linear in the particle count instead of quadratic.
   */
  private forPairs(range: number, fn: (a: Particle, b: Particle, d2: number) => void, ps: Particle[] = this.particles): void {
    const cols = Math.ceil(this.width / range) + 1;
    const rows = Math.ceil(this.height / range) + 1;
    // grow-only buffers: this runs twice a frame, and a fresh typed array each time is garbage the browser must collect
    if (this.pairHead.length < cols * rows) this.pairHead = new Int32Array(cols * rows);
    if (this.pairNext.length < ps.length) this.pairNext = new Int32Array(ps.length + 256);
    const head = this.pairHead;
    const next = this.pairNext;
    head.fill(-1, 0, cols * rows);
    for (let i = 0; i < ps.length; i++) {
      const cell = Math.floor(ps[i].y / range) * cols + Math.floor(ps[i].x / range);
      next[i] = head[cell];
      head[cell] = i;
    }
    const r2 = range * range;
    for (let i = 0; i < ps.length; i++) {
      const a = ps[i];
      const cx = Math.floor(a.x / range);
      const cy = Math.floor(a.y / range);
      for (let y = Math.max(0, cy - 1); y <= Math.min(rows - 1, cy + 1); y++) {
        for (let x = Math.max(0, cx - 1); x <= Math.min(cols - 1, cx + 1); x++) {
          for (let j = head[y * cols + x]; j !== -1; j = next[j]) {
            if (j <= i) continue; // each pair once
            const b = ps[j];
            const dx = b.x - a.x;
            const dy = b.y - a.y;
            const d2 = dx * dx + dy * dy;
            if (d2 < r2) fn(a, b, d2);
          }
        }
      }
    }
  }

  /** Touching particles repel; when allowed, the contact fires a strike. */
  private contacts(): void {
    if (this.strikeGap > 0) this.strikeGap--;
    // Collisions run every other step with a doubled push: same motion, half the pair search.
    this.contactSkip = !this.contactSkip;
    if (this.contactSkip) {
      return;
    }
    // dots of the word sit closer than the contact radius: they take no part in collisions or strikes
    const free = this.formedCount > 0 ? this.particles.filter((p) => !p.formed) : this.particles;
    const contact = TIERS[this.tier].contact;
    this.forPairs(
      contact,
      (a, b, d2) => {
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const d = Math.sqrt(d2) || 1;
        // soft collision: push apart in proportion to overlap
        const push = (1 - d / contact) * 0.7;
        a.vx -= (dx / d) * push;
        a.vy -= (dy / d) * push;
        b.vx += (dx / d) * push;
        b.vy += (dy / d) * push;
        if (!this.game && a.cd === 0 && b.cd === 0 && this.strikeGap === 0 && this.strikes.length < MAX_STRIKES) {
          this.fire(a, b);
          a.cd = b.cd = 90 + Math.round(Math.random() * 90);
          this.strikeGap = STRIKE_GAP_MIN + Math.round(Math.random() * STRIKE_GAP_RANGE);
        }
      },
      free,
    );
  }

  // ------------------------------------------------------------------ game host

  get held(): boolean {
    return !!this.hold;
  }

  /** The login card in stage pixels, re-measured at most twice a second (layout reads are not free). */
  card(): Rect | null {
    if (this.tick - this.cardAt > 30) {
      this.cardAt = this.tick;
      const stage = this.host.nativeElement.getBoundingClientRect();
      const r = this.host.nativeElement.closest(".nl-login")?.querySelector(".nl-login__card")?.getBoundingClientRect();
      this.cardRect = r ? { x: r.left - stage.left, y: r.top - stage.top, w: r.width, h: r.height } : null;
    }
    return this.cardRect;
  }

  label(key: string): string {
    return this.translate.instant(key);
  }

  /** A strike out of (x, y); two throwaway particles stand in for the contact that normally sets one off. */
  lightning(x: number, y: number): void {
    if (this.strikes.length < MAX_STRIKES + 2) {
      this.fire(this.makeParticle(x - 3, y), this.makeParticle(x + 3, y));
    }
  }

  refill(n = 8): void {
    for (let i = 0; i < n && this.particles.length < this.target; i++) {
      const edge = Math.random() < 0.5;
      this.particles.push(
        this.makeParticle(
          edge ? (Math.random() < 0.5 ? 0 : this.width) : Math.random() * this.width,
          edge ? Math.random() * this.height : Math.random() < 0.5 ? 0 : this.height,
        ),
      );
    }
  }

  lock(dots: [number, number][]): Particle[] {
    return this.assignDots(dots);
  }

  unlock(): void {
    for (const p of this.particles) p.formed = false;
    this.formedCount = 0;
  }

  // ------------------------------------------------------------------ typed word

  /** Reads the username field and re-spells the word when its text changed. */
  private syncWord(): void {
    const field = this.usernameField();
    if (!field || this.reducedMotion || this.snake || this.game) {
      return;
    }
    const word = field.value.trim().slice(0, WORD_MAX_CHARS);
    if (word === this.word) {
      return;
    }
    this.word = word;
    if (!this.wordPending) {
      this.wordPending = window.setTimeout(() => {
        this.wordPending = 0;
        this.formWord();
      }, WORD_DEBOUNCE_MS);
    }
  }

  /**
   * Samples the word, drawn big across the middle of the stage, into dots and
   * hands each dot to the nearest free particle. Letters that are still there
   * keep their particles (they are the nearest); particles of a deleted letter
   * are released with a burst, and new ones fly in with a flash.
   */
  private formWord(): void {
    this.assignDots(this.sampleWord());
  }

  /** Pins the nearest free particle to each dot (in dot order) and releases the ones that no longer hold a dot. */
  private assignDots(targets: [number, number][]): Particle[] {
    const ps = this.particles;
    const assigned: Particle[] = [];
    const before = new Set(ps.filter((p) => p.formed));
    for (const p of ps) p.formed = false;

    // free particles in a grid, so each dot finds its nearest one without scanning them all
    const cell = 48;
    const cols = Math.ceil(this.width / cell) + 1;
    const rows = Math.ceil(this.height / cell) + 1;
    const grid: Particle[][] = Array.from({ length: cols * rows }, () => []);
    const at = (v: number, max: number) => Math.min(max - 1, Math.max(0, Math.floor(v / cell)));
    for (const p of ps) {
      grid[at(p.y, rows) * cols + at(p.x, cols)].push(p);
    }
    let flashes = 0;
    let count = 0;
    for (const [x, y] of targets) {
      const cx = at(x, cols);
      const cy = at(y, rows);
      let best: Particle | null = null;
      let bestD = Infinity;
      let found = -1;
      // ring by ring around the dot, perimeter cells only; one ring past the first hit for a better pick
      for (let ring = 0; ring <= WORD_SEARCH_RINGS && (found < 0 || ring <= found + 1); ring++) {
        for (let gy = cy - ring; gy <= cy + ring; gy++) {
          if (gy < 0 || gy >= rows) continue;
          const edge = gy === cy - ring || gy === cy + ring;
          for (let gx = cx - ring; gx <= cx + ring; gx += edge || ring === 0 ? 1 : ring * 2) {
            if (gx < 0 || gx >= cols) continue;
            for (const p of grid[gy * cols + gx]) {
              if (p.formed) continue;
              const d = (p.x - x) ** 2 + (p.y - y) ** 2;
              if (d < bestD) {
                bestD = d;
                best = p;
                if (found < 0) found = ring;
              }
            }
          }
        }
      }
      if (!best) {
        // nothing near: any particle still free
        best = ps.find((p) => !p.formed) ?? null;
        if (!best) break;
      }
      best.formed = true;
      best.scatter = 0;
      best.tx = x;
      best.ty = y;
      count++;
      assigned.push(best);
      if (!before.has(best) && flashes++ < 120) best.glow = 18;
    }
    this.formedCount = count;

    // Released: each particle heads for a random spot of the stage and drifts freely once its time is up. A plain
    // burst would be cut to the drift speed a step later and leave them piled where the word was.
    for (const p of before) {
      if (p.formed) continue;
      p.formed = true;
      p.tx = Math.random() * this.width;
      p.ty = Math.random() * this.height;
      p.scatter = 50 + Math.floor(Math.random() * 50);
    }
    return assigned;
  }

  /**
   * Dots (x, y) covering the glyphs of the current word, at most WORD_SHARE of the
   * particles. The word sits in the band between the top keys and the logo.
   */
  private sampleWord(): [number, number][] {
    if (!this.word) {
      return [];
    }
    const stage = this.host.nativeElement.getBoundingClientRect();
    const logo = this.host.nativeElement.closest(".nl-login")?.querySelector(".nl-login__logo img")?.getBoundingClientRect();
    const top = 64;
    const bottom = logo ? logo.top - stage.top - 28 : this.height * 0.3;
    const avail = Math.max(70, bottom - top);
    const centerY = top + avail / 2;

    const canvas = (this.glyphCanvas ??= document.createElement("canvas"));
    const c = canvas.getContext("2d", { willReadFrequently: true });
    if (!c) {
      return [];
    }
    const font = (px: number) => `700 ${px}px "Chakra Petch", "Figtree", sans-serif`;
    let size = Math.min(avail / 1.1, 200);
    c.font = font(size);
    const fit = (this.width * 0.9) / Math.max(1, c.measureText(this.word).width);
    size = Math.floor(size * Math.min(1, fit));

    // smaller type needs tighter dots to stay readable
    const step = Math.max(3, Math.min(WORD_STEP, Math.round(size / 18)));
    const bandH = Math.ceil(size * 1.3);
    canvas.width = Math.ceil(this.width);
    canvas.height = bandH;
    c.font = font(size);
    c.textAlign = "center";
    c.textBaseline = "middle";
    c.fillText(this.word, canvas.width / 2, bandH / 2);
    const data = c.getImageData(0, 0, canvas.width, bandH).data;
    const y0 = centerY - bandH / 2;
    const dots: [number, number][] = [];
    for (let y = 0; y < bandH; y += step) {
      for (let x = 0; x < canvas.width; x += step) {
        if (data[(y * canvas.width + x) * 4 + 3] > 128) {
          dots.push([x + (Math.random() - 0.5) * 2, y0 + y + (Math.random() - 0.5) * 2]);
        }
      }
    }
    const cap = Math.floor(this.particles.length * WORD_SHARE);
    if (dots.length > cap) {
      // too many for the field: keep a random subset, the word stays readable but sparser
      for (let i = dots.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [dots[i], dots[j]] = [dots[j], dots[i]];
      }
      dots.length = cap;
    }
    return dots;
  }

  // ------------------------------------------------------------------ snake

  private canPlay(): boolean {
    return !this.reducedMotion && this.desktopQuery.matches;
  }

  /** Starts (or restarts) a game; called by the Play menu. */
  play(id: GameId = "snake"): void {
    if (!this.canPlay()) {
      return;
    }
    (document.activeElement as HTMLElement | null)?.blur();
    this.halt();
    this.clearWord();
    if (id === "snake") {
      this.start(0);
    } else {
      this.cardAt = -999;
      this.game = createGame(id, this);
      this.game.start();
      this.zone.run(() => this.activeId.set(id));
      this.setPlaying(true);
    }
    this.zone.run(() => this.activeId.set(id));
  }

  /** Leaves the game and lets the typed word spell itself again. */
  stop(): void {
    this.halt();
    this.syncWord();
  }

  private halt(): void {
    this.snake = null;
    this.game?.stop();
    this.game = null;
    this.hold = null;
    this.setPlaying(false);
    if (this.activeId() !== null) {
      this.zone.run(() => this.activeId.set(null));
    }
  }

  /** The typed word makes way for a game. */
  private clearWord(): void {
    if (this.word) {
      this.word = "";
      this.formWord();
    }
  }

  private setPlaying(on: boolean): void {
    if (this.playing() !== on) {
      // the loop runs outside Angular: re-enter it so the page's hint updates
      this.zone.run(() => this.playing.set(on));
    }
  }

  private start(angle: number): void {
    this.snake = {
      x: this.width * (0.15 + Math.random() * 0.7),
      y: this.height * (0.1 + Math.random() * 0.25),
      angle,
      want: angle,
      pts: [],
      len: SNAKE_START_LEN,
      score: 0,
      level: 1,
      best: this.readBest(),
      toxics: [],
      over: 0,
    };
    this.setPlaying(true);
  }

  private readBest(): number {
    try {
      return Number(localStorage.getItem(BEST_KEY)) || 0;
    } catch {
      return 0;
    }
  }

  private die(sn: Snake): void {
    sn.over = 1;
    if (sn.score > sn.best) {
      sn.best = sn.score;
      try {
        localStorage.setItem(BEST_KEY, String(sn.best));
      } catch {
        // private mode: the record just lasts this game
      }
    }
  }

  /** Steers and moves the head, eats what it touches and ends the game on a self bite or a toxic hit. */
  private stepSnake(): void {
    const sn = this.snake;
    if (!sn) {
      return;
    }
    if (sn.over > 0) {
      sn.over++;
      return;
    }
    const speed = Math.min(SNAKE_MAX_SPEED, SNAKE_BASE_SPEED + (sn.level - 1) * SPEED_PER_LEVEL);
    // turning radius stays ~25px at any speed: faster levels are harder to steer, not impossible
    const turn = speed * 0.04;
    sn.angle += Math.max(-turn, Math.min(turn, angleDiff(sn.angle, sn.want)));
    sn.x = (sn.x + Math.cos(sn.angle) * speed + this.width) % this.width;
    sn.y = (sn.y + Math.sin(sn.angle) * speed + this.height) % this.height;

    const pts = sn.pts;
    const n = pts.length;
    if (n === 0 || Math.hypot(sn.x - pts[n - 2], sn.y - pts[n - 1]) >= SNAKE_SPACING) {
      pts.push(sn.x, sn.y);
      // drop the tail in chunks, not one shift per point
      if (pts.length > (sn.len + 200) * 2) {
        pts.splice(0, pts.length - sn.len * 2);
      }
    }

    const ps = this.particles;
    for (let i = ps.length - 1; i >= 0; i--) {
      const p = ps[i];
      const dx = sn.x - p.x;
      const dy = sn.y - p.y;
      const d2 = dx * dx + dy * dy;
      if (d2 < (SNAKE_EAT + p.r) ** 2) {
        ps[i] = ps[ps.length - 1];
        ps.pop();
        sn.score++;
        sn.len += SNAKE_GROW;
      } else if (d2 < SNAKE_PULL * SNAKE_PULL) {
        const d = Math.sqrt(d2);
        const f = (1 - d / SNAKE_PULL) * 0.12;
        p.vx += (dx / d) * f;
        p.vy += (dy / d) * f;
      }
    }
    // refill from the edges, one every few steps, so the field never runs dry
    if (ps.length < this.target && (this.tick & 3) === 0) {
      const edge = Math.random() < 0.5;
      ps.push(
        this.makeParticle(
          edge ? (Math.random() < 0.5 ? 0 : this.width) : Math.random() * this.width,
          edge ? Math.random() * this.height : Math.random() < 0.5 ? 0 : this.height,
        ),
      );
    }

    sn.level = 1 + Math.floor(sn.score / LEVEL_EVERY);
    while (sn.toxics.length < Math.min(MAX_TOXIC, sn.level - 1)) {
      this.spawnToxic(sn);
    }
    for (const t of sn.toxics) {
      t.x += t.vx;
      t.y += t.vy;
      if (t.x < 0 || t.x > this.width) t.vx = -t.vx;
      if (t.y < 0 || t.y > this.height) t.vy = -t.vy;
      if (Math.hypot(t.x - sn.x, t.y - sn.y) < SNAKE_RADIUS + 5) {
        this.die(sn);
        return;
      }
    }

    for (let i = 0; i < pts.length - SNAKE_NECK * 2; i += 2) {
      if (i < pts.length - sn.len * 2) continue;
      const dx = pts[i] - sn.x;
      const dy = pts[i + 1] - sn.y;
      if (dx * dx + dy * dy < SNAKE_RADIUS * SNAKE_RADIUS) {
        this.die(sn);
        return;
      }
    }
  }

  /** A toxic particle appears at least 250px from the head, drifting in a random direction. */
  private spawnToxic(sn: Snake): void {
    let x = 0;
    let y = 0;
    for (let tries = 0; tries < 10; tries++) {
      x = Math.random() * this.width;
      y = Math.random() * this.height;
      if (Math.hypot(x - sn.x, y - sn.y) > 250) break;
    }
    const a = Math.random() * Math.PI * 2;
    sn.toxics.push({ x, y, vx: Math.cos(a) * 0.7, vy: Math.sin(a) * 0.7 });
  }

  private drawSnake(ctx: CanvasRenderingContext2D, sn: Snake): void {
    const dead = sn.over > 0;
    const fade = dead ? Math.max(0.25, 1 - sn.over / 90) : 1;
    ctx.save();

    // toxic particles: a pulsing red core in a ring
    for (const t of sn.toxics) {
      const pulse = 1 + Math.sin(this.tick * 0.12 + t.x) * 0.25;
      ctx.shadowColor = "#ff3b4e";
      ctx.shadowBlur = 16;
      ctx.fillStyle = "#ff3b4e";
      ctx.globalAlpha = 0.95;
      ctx.beginPath();
      ctx.arc(t.x, t.y, 3.2, 0, Math.PI * 2);
      ctx.fill();
      ctx.shadowBlur = 0;
      ctx.strokeStyle = "#ff3b4e";
      ctx.globalAlpha = 0.55;
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.arc(t.x, t.y, 7 * pulse, 0, Math.PI * 2);
      ctx.stroke();
    }

    // body: dark outline pass, then the skin, then dorsal spots; every other stored point is a circle
    const pts = sn.pts;
    const first = Math.max(0, pts.length - sn.len * 2);
    const count = (pts.length - first) / 2;
    const radius = (k: number) => SNAKE_RADIUS * Math.min(1, 0.3 + (0.7 * k) / 40);
    ctx.globalAlpha = fade;
    ctx.fillStyle = "rgba(0, 0, 0, 0.55)";
    for (let k = 0; k < count; k += 2) {
      ctx.beginPath();
      ctx.arc(pts[first + k * 2], pts[first + k * 2 + 1], radius(k) + 1.4, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.shadowColor = this.color;
    ctx.shadowBlur = 10;
    ctx.fillStyle = this.color;
    for (let k = 0; k < count; k += 2) {
      ctx.beginPath();
      ctx.arc(pts[first + k * 2], pts[first + k * 2 + 1], radius(k), 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.shadowBlur = 0;
    // lighter back stripe + darker diamond spots every ~24px, turned along the body
    ctx.fillStyle = "rgba(255, 255, 255, 0.22)";
    for (let k = 1; k < count; k += 2) {
      ctx.beginPath();
      ctx.arc(pts[first + k * 2], pts[first + k * 2 + 1], radius(k) * 0.4, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.fillStyle = "rgba(0, 0, 0, 0.3)";
    for (let k = 4; k < count - 2; k += 6) {
      const i = first + k * 2;
      const dx = pts[i + 2] - pts[i - 2];
      const dy = pts[i + 3] - pts[i - 1];
      if (Math.abs(dx) > 30 || Math.abs(dy) > 30) continue; // across a wrap
      const r = radius(k);
      ctx.beginPath();
      ctx.ellipse(pts[i], pts[i + 1], r * 0.62, r * 0.4, Math.atan2(dy, dx), 0, Math.PI * 2);
      ctx.fill();
    }

    // head: turned along the heading, with eyes and a flicking forked tongue
    ctx.translate(sn.x, sn.y);
    ctx.rotate(sn.angle);
    if ((this.tick & 63) < 14) {
      ctx.strokeStyle = "#ff5a6a";
      ctx.lineWidth = 1.2;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(9, 0);
      ctx.lineTo(16, 0);
      ctx.moveTo(16, 0);
      ctx.lineTo(20, -3);
      ctx.moveTo(16, 0);
      ctx.lineTo(20, 3);
      ctx.stroke();
    }
    ctx.shadowColor = this.color;
    ctx.shadowBlur = 14;
    ctx.fillStyle = "rgba(0, 0, 0, 0.55)";
    ctx.beginPath();
    ctx.ellipse(2, 0, 11.4, 8.8, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = this.color;
    ctx.beginPath();
    ctx.ellipse(2, 0, 10, 7.4, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.fillStyle = "rgba(255, 255, 255, 0.2)";
    ctx.beginPath();
    ctx.ellipse(3, 0, 6, 3.4, 0, 0, Math.PI * 2);
    ctx.fill();
    for (const side of [-1, 1]) {
      ctx.fillStyle = "#ffffff";
      ctx.beginPath();
      ctx.arc(4.5, side * 4.2, 2.3, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = dead ? "#ff3b4e" : "#0b0b10";
      ctx.beginPath();
      ctx.arc(5.2, side * 4.2, 1.1, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();

    // HUD (top-left) and the game-over card
    const label = (key: string) => this.translate.instant(key).toUpperCase();
    ctx.save();
    ctx.font = "600 12px ui-monospace, SFMono-Regular, Menlo, monospace";
    ctx.fillStyle = this.color;
    ctx.globalAlpha = 0.85;
    ctx.fillText(
      `${label("GENERAL.SNAKE_SCORE")} ${sn.score}   ${label("GENERAL.SNAKE_LEVEL")} ${sn.level}   ${label("GENERAL.SNAKE_BEST")} ${sn.best}`,
      20,
      28,
    );
    if (dead) {
      ctx.textAlign = "center";
      ctx.globalAlpha = Math.min(1, sn.over / 30);
      ctx.font = "700 30px ui-monospace, SFMono-Regular, Menlo, monospace";
      ctx.fillText(`${label("GENERAL.SNAKE_OVER")} · ${sn.score}`, this.width / 2, this.height * 0.2);
      ctx.font = "500 13px ui-monospace, SFMono-Regular, Menlo, monospace";
      ctx.globalAlpha *= 0.8;
      ctx.fillText(this.translate.instant("GENERAL.SNAKE_AGAIN"), this.width / 2, this.height * 0.2 + 26);
    }
    ctx.restore();
  }

  // ------------------------------------------------------------------ lightning

  /**
   * The strike starts between the two particles and runs far: 2–3 main channels
   * to particles 180–450px away, and from some of them one more hop. Each channel
   * is a fractal bolt (see `channel`) that forks into thinner and thinner branches.
   */
  private fire(a: Particle, b: Particle): void {
    const ox = (a.x + b.x) / 2;
    const oy = (a.y + b.y) / 2;
    const segments: Segment[] = [];
    const hits: { p: Particle; at: number }[] = [
      { p: a, at: 0 },
      { p: b, at: 0 },
    ];
    const used = new Set<Particle>([a, b]);
    const channels = 2 + Math.round(Math.random());
    for (let c = 0; c < channels; c++) {
      const n = this.farPick(ox, oy, 180, 450, used);
      if (!n) continue;
      used.add(n);
      const len = this.channel(ox, oy, n.x, n.y, 0, 0, segments);
      hits.push({ p: n, at: len });
      if (Math.random() < 0.5) {
        const m = this.farPick(n.x, n.y, 100, 260, used);
        if (m) {
          used.add(m);
          hits.push({ p: m, at: this.channel(n.x, n.y, m.x, m.y, len, 0, segments) });
        }
      }
    }
    if (segments.length === 0) {
      // nobody far enough: a short spark between the pair only
      this.channel(a.x, a.y, b.x, b.y, 0, 0, segments);
    }
    const reach = segments.reduce((m, s) => Math.max(m, s.at), 0);
    this.strikes.push({ segments, reach, age: 0, hold: HOLD, hits, ox, oy });
  }

  /** A random particle between `min` and `max` px from (x, y); a few tries, `null` if the stage is too small. */
  private farPick(x: number, y: number, min: number, max: number, skip: Set<Particle>): Particle | null {
    const ps = this.particles;
    for (let tries = 0; tries < 60; tries++) {
      const p = ps[Math.floor(Math.random() * ps.length)];
      if (!p || skip.has(p)) continue;
      const d = Math.hypot(p.x - x, p.y - y);
      if (d >= min && d <= max) return p;
    }
    return null;
  }

  /**
   * One bolt from (x1, y1) to (x2, y2) by midpoint displacement: the rough,
   * jagged path of a real discharge (offset shrinks by 0.55 per round, so the kinks
   * stay sharp). Along it, branches fork off in the travel direction, each a
   * smaller bolt of its own with fewer rounds and a shorter reach, down to depth 3.
   * Returns the bolt's end distance from the strike origin.
   */
  private channel(x1: number, y1: number, x2: number, y2: number, startAt: number, depth: number, out: Segment[]): number {
    const length = Math.hypot(x2 - x1, y2 - y1);
    let pts: [number, number][] = [
      [x1, y1],
      [x2, y2],
    ];
    let offset = length * (depth === 0 ? 0.3 : 0.38);
    // main bolts get 7 rounds (128 pieces); each branch level has fewer, so detail follows size
    const rounds = Math.max(3, 7 - depth * 2);
    for (let round = 0; round < rounds; round++) {
      const next: [number, number][] = [pts[0]];
      for (let k = 0; k < pts.length - 1; k++) {
        const [ax, ay] = pts[k];
        const [bx, by] = pts[k + 1];
        const len = Math.hypot(bx - ax, by - ay) || 1;
        const shift = (Math.random() - 0.5) * offset;
        next.push([(ax + bx) / 2 + (-(by - ay) / len) * shift, (ay + by) / 2 + ((bx - ax) / len) * shift]);
        next.push(pts[k + 1]);
      }
      pts = next;
      offset *= 0.55;
    }

    let at = startAt;
    const forkChance = [0.05, 0.04, 0.03][depth] ?? 0;
    for (let k = 0; k < pts.length - 1; k++) {
      const [ax, ay] = pts[k];
      const [bx, by] = pts[k + 1];
      at += Math.hypot(bx - ax, by - ay);
      out.push({ x1: ax, y1: ay, x2: bx, y2: by, at, depth });

      // fork: the branch leans away from the travel direction, shorter and thinner than its parent
      if (depth < 3 && k > 2 && k < pts.length - 4 && Math.random() < forkChance) {
        const dir = Math.atan2(by - ay, bx - ax) + (Math.random() < 0.5 ? 1 : -1) * (0.25 + Math.random() * 0.5);
        const fl = length * (0.16 + Math.random() * 0.24) * (1 - depth * 0.25);
        this.channel(bx, by, bx + Math.cos(dir) * fl, by + Math.sin(dir) * fl, at, depth + 1, out);
      }
    }
    return at;
  }

  // ------------------------------------------------------------------ drawing

  private draw(): void {
    const ctx = this.ctx;
    if (!ctx) {
      return;
    }
    const { particles: ps, mouse, color } = this;
    ctx.clearRect(0, 0, this.width, this.height);
    // Strikes go first: they sit behind the links and the particles, as if in the background of the field.
    for (const s of this.strikes) {
      this.drawStrike(ctx, s);
    }
    ctx.strokeStyle = color;
    ctx.fillStyle = color;

    // Links are bucketed by alpha: one path and one stroke per bucket, not per link.
    ctx.lineWidth = 0.6;
    const buckets: Path2D[] = Array.from({ length: LINK_ALPHA_BUCKETS }, () => new Path2D());
    const reach = TIERS[this.tier].link;
    // The grid search runs every LINK_REUSE-th frame; the frames between redraw the same pairs at their new positions
    // (particles move ~0.5px a frame). While a word is up, its packed dots link only to their closest neighbours in
    // a second, short-range pass: at the full range each would carry ~140 lines.
    const word = this.formedCount > 0;
    if (this.linkAge++ % LINK_REUSE === 0 || this.linkReach !== reach || this.linkWord !== word) {
      this.linkReach = reach;
      this.linkWord = word;
      this.buildLinks(this.linkSets[0], reach, word ? ps.filter((p) => !p.formed) : ps);
      this.buildLinks(this.linkSets[1], WORD_LINK, word ? ps.filter((p) => p.formed) : []);
    }
    for (const { a, b, k } of this.linkSets) {
      for (let i = 0; i < a.length; i++) {
        buckets[k[i]].moveTo(a[i].x, a[i].y);
        buckets[k[i]].lineTo(b[i].x, b[i].y);
      }
    }
    buckets.forEach((path, k) => {
      ctx.globalAlpha = ((k + 0.5) / LINK_ALPHA_BUCKETS) * 0.24;
      ctx.stroke(path);
    });

    if (mouse.active) {
      // soft light following the cursor
      const halo = ctx.createRadialGradient(mouse.x, mouse.y, 0, mouse.x, mouse.y, 220);
      halo.addColorStop(0, color);
      halo.addColorStop(1, "transparent");
      ctx.globalAlpha = 0.12;
      ctx.fillStyle = halo;
      ctx.fillRect(mouse.x - 220, mouse.y - 220, 440, 440);
      ctx.fillStyle = color;

      // wires from the cursor to the particles near it: 4 alpha buckets, 4 strokes (one stroke per wire costs hundreds)
      ctx.lineWidth = 1;
      const wires = [new Path2D(), new Path2D(), new Path2D(), new Path2D()];
      for (const p of ps) {
        const dx = p.x - mouse.x;
        const dy = p.y - mouse.y;
        const d2 = dx * dx + dy * dy;
        if (d2 < 190 * 190) {
          const k = Math.min(3, Math.floor((1 - Math.sqrt(d2) / 190) * 4));
          wires[k].moveTo(p.x, p.y);
          wires[k].lineTo(mouse.x, mouse.y);
        }
      }
      wires.forEach((w, k) => {
        ctx.globalAlpha = ((k + 0.5) / 4) * 0.9;
        ctx.stroke(w);
      });
    }

    // Resting particles go out as ONE path of tiny squares (at 1-3px a square is a dot, and cheaper than an arc);
    // the ones lit by a strike get a soft halo made of a second, larger, fainter circle — a canvas shadow per
    // dot is what a dense field, or a typed word flashing a hundred of them, cannot afford.
    ctx.globalAlpha = 0.85;
    const dots = new Path2D();
    for (const p of ps) {
      if (p.glow === 0) {
        dots.rect(p.x - p.r, p.y - p.r, p.r * 2, p.r * 2);
      }
    }
    ctx.fill(dots);
    for (const p of ps) {
      if (p.glow === 0) continue;
      const lit = p.glow / 18;
      ctx.fillStyle = color;
      ctx.globalAlpha = 0.28 * lit;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r + 3 + lit * 7, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#ffffff";
      ctx.globalAlpha = 0.9;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r + lit * 2.2, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.fillStyle = color;

    if (this.snake) {
      this.drawSnake(ctx, this.snake);
    }
    this.game?.draw(ctx);

    if (this.hold && this.hold.t > HOLD_STEPS && mouse.active) {
      // two rings closing in on the cursor: the pull is on
      ctx.lineWidth = 1.2;
      for (let k = 0; k < 2; k++) {
        const phase = ((this.tick * 1.5 + k * 30) % 60) / 60;
        ctx.globalAlpha = (1 - phase) * 0.5;
        ctx.beginPath();
        ctx.arc(mouse.x, mouse.y, 70 * (1 - phase) + 6, 0, Math.PI * 2);
        ctx.stroke();
      }
    }

    if (this.shock) {
      const { x, y, t } = this.shock;
      ctx.globalAlpha = Math.max(0, 0.5 - t / 80);
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(x, y, t * 14, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
  }

  /**
   * Only the part the front has reached is drawn. Two passes per depth — a wide
   * glow in the signal colour and a thin white core — with one path each, so a
   * strike costs a handful of strokes whatever its segment count.
   */
  /**
   * Drawn in groups of one path each (depth x position along the strike, so the
   * trunk thins toward its end and each branch level is thinner than its parent),
   * and three strokes per group: a wide faint halo, a glow, and a thin white core.
   * A flash sits at the origin while the strike grows.
   */
  private drawStrike(ctx: CanvasRenderingContext2D, s: Strike): void {
    const front = s.age * SPEED;
    const grown = front >= s.reach;
    // flicker while holding, then fade out over the last frames
    const fade = (grown ? (s.hold / HOLD) * (0.6 + Math.random() * 0.4) : 1) * STRIKE_DIM;
    const tips: [number, number][] = [];

    // [core, glow] widths per group
    const WIDTHS: Record<string, [number, number]> = {
      "0:0": [1.7, 6],
      "0:1": [1.3, 4.6],
      "0:2": [0.9, 3.2],
      "1:0": [0.9, 3],
      "1:1": [0.7, 2.4],
      "2:0": [0.6, 2],
      "3:0": [0.45, 1.5],
    };
    const paths: Record<string, Path2D> = {};
    for (const seg of s.segments) {
      const len = Math.hypot(seg.x2 - seg.x1, seg.y2 - seg.y1);
      const start = seg.at - len;
      if (start > front) continue;
      // the piece the front is crossing is drawn only up to it, so the bolt creeps instead of popping
      const t = seg.at > front && len > 0 ? (front - start) / len : 1;
      const ex = seg.x1 + (seg.x2 - seg.x1) * t;
      const ey = seg.y1 + (seg.y2 - seg.y1) * t;
      const bin =
        seg.depth === 0 ? Math.min(2, Math.floor((seg.at / s.reach) * 3)) : seg.depth === 1 ? (seg.at / s.reach < 0.6 ? 0 : 1) : 0;
      const key = `${seg.depth}:${bin}`;
      const path = (paths[key] ??= new Path2D());
      path.moveTo(seg.x1, seg.y1);
      path.lineTo(ex, ey);
      if (t < 1 && seg.depth <= 1) tips.push([ex, ey]);
    }

    ctx.save();
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    for (const [key, path] of Object.entries(paths)) {
      const [core, glow] = WIDTHS[key];
      // halo, glow, core: three strokes, no canvas shadow (a blur per bolt is what slow GPUs choke on)
      ctx.strokeStyle = this.color;
      ctx.globalAlpha = 0.1 * fade;
      ctx.lineWidth = glow * 2.6;
      ctx.stroke(path);
      ctx.globalAlpha = 0.4 * fade;
      ctx.lineWidth = glow;
      ctx.stroke(path);
      ctx.strokeStyle = "#ffffff";
      ctx.globalAlpha = Math.min(1, 0.95 * fade);
      ctx.lineWidth = core;
      ctx.stroke(path);
    }

    // origin flash: bright while the bolt grows, dying with it
    const flash = fade * (0.75 + 0.25 * Math.sin(s.age * 0.9));
    const fr = 34 + Math.min(1, front / Math.max(1, s.reach)) * 22;
    const g = ctx.createRadialGradient(s.ox, s.oy, 0, s.ox, s.oy, fr);
    g.addColorStop(0, "rgba(255, 255, 255, 0.9)");
    g.addColorStop(0.25, this.color);
    g.addColorStop(1, "transparent");
    ctx.globalAlpha = 0.55 * flash;
    ctx.fillStyle = g;
    ctx.fillRect(s.ox - fr, s.oy - fr, fr * 2, fr * 2);

    // a bright head on the main channels still growing
    for (const [x, y] of tips.slice(0, 12)) {
      ctx.fillStyle = this.color;
      ctx.globalAlpha = 0.35;
      ctx.beginPath();
      ctx.arc(x, y, 6, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#ffffff";
      ctx.globalAlpha = 0.95;
      ctx.beginPath();
      ctx.arc(x, y, 2.2, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }
}
